import { GetQueryParamsWithFilterArray, GetQueryResponse } from '../../../types/api.type';
import { CreateOrder, ManualOrder, Order, UpsertOrder } from '../../../types/orders.type';
import { sfcApi } from '../../api';

export const ordersApi = sfcApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllOrders: builder.query<GetQueryResponse, GetQueryParamsWithFilterArray | void>({
      query: (params) => ({
        url: `orders${params?.scope ? `?scope=${params.scope}` : ''}`,
        method: 'POST',
        body: params?.data
      }),
      providesTags: ['Order']
    }),
    getOrder: builder.query<{ order: Order }, number>({
      query: (id) => `orders/${id}`,
      providesTags: (result, error, id) => [{ type: 'Order', id }]
    }),
    generateOrder: builder.query<{ order: Order }, number>({
      query: (id) => `orders/generate-invoice/${id}`
    }),
    createOrder: builder.mutation<{ order: Order }, CreateOrder>({
      query: (data) => ({
        url: `orders/create`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: (result, error, arg) => [
        'Order',
        { type: 'Parent', id: arg.parent_id },
        { type: 'Instructors', id: arg.parent_id },
        { type: 'Admins', id: arg.parent_id },
      ]
    }),
    createOrders: builder.mutation<{ order: ManualOrder }, CreateOrder>({
      query: (data) => ({
        url: `orders/create`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: ['Order']
    }),
    upsertOrder: builder.mutation<{ order: Order }, UpsertOrder>({
      query: (data) => ({
        url: `orders/upsert`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: ['Order']
    }),
    updateOrderTransaction: builder.mutation<
      { order: Order },
      { ids: number[]; transaction_id: string; last_4?: number }
    >({
      query: (params) => ({
        url: `orders/updateTransactionId`,
        method: 'PUT',
        body: params
      }),
      invalidatesTags: ['Order']
    }),
    deleteOrder: builder.mutation<{ order: Order }, number>({
      query: (id) => ({
        url: `orders/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Order', id }, 'Order']
    }),
    notifyOrder: builder.mutation<string, number>({
      query: (id) => ({
        url: `orders/notify-order/${id}`,
        method: 'GET',
        responseHandler: async (response) => {
          const text = await response.text();
          if (!response.ok) {
            try {
              return JSON.parse(text);
            } catch {
              return { message: text || 'Failed to send confirmation email' };
            }
          }
          return text;
        }
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Order', id }]
    })
  })
});

export const {
  useGetAllOrdersQuery,
  useGetOrderQuery,
  useGenerateOrderQuery,
  useCreateOrderMutation,
  useCreateOrdersMutation,
  useUpsertOrderMutation,
  useUpdateOrderTransactionMutation,
  useDeleteOrderMutation,
  useNotifyOrderMutation
} = ordersApi;
